import type { Direction } from './direction';
import type { EntityId } from './entity';
import type { TilePoint } from './mobile';
import type { World } from './world';
import { EMOTE_CLIP_IDS } from './content';
import type { CardTarget } from './cardGame';
import type { StarterRecipeId } from './cardContent';
import type { SpherePotionId, SphereSpellId } from './sphereDuel';
import type { CombatLabScenarioId } from './combatLab';

/**
 * A command (a.k.a. intent) is a plain, serializable description of "something
 * that should happen". Input and the network never mutate entities directly;
 * they {@link World.enqueue} commands, and the {@link World.tick} drains them in
 * FIFO order at the start of each fixed step, applying each through the pure
 * reducer {@link applyCommand}. This queue is the single seam the authoritative
 * server owns, so commands stay plain data: no functions, no class instances,
 * only JSON-friendly fields.
 *
 * Quarried to walk-around: the only intent a client can originate is movement.
 * The richer command set (combat, items, interaction) was left behind in
 * `ultima` (see ADR-0001).
 */

/**
 * Request that an entity move (or stop). Wraps the existing, already-faithful
 * {@link Mobile} intent: `dir` is the direction to head (`null` to stop on the
 * next tile boundary), `running` selects the run gait. The movement system
 * samples `running` when the next step begins.
 *
 * `turnOnly` (optional, default false) requests a *turn-in-place*: the mobile
 * pivots to face `dir` and then idles, never stepping off its tile even while
 * the intent is held. This is the seam that lets the mouse spin you on the spot
 * (right-click and circle the cursor within your own tile) without reintroducing
 * the oscillation PR #56 fixed - a turn-only intent can never move (see ADR-0022).
 * Omitting it keeps the wire shape backward-compatible, so keyboard and NPC
 * senders are unaffected.
 */
export interface MoveIntentCommand {
  kind: 'MoveIntent';
  entity: EntityId;
  dir: Direction | null;
  running: boolean;
  turnOnly?: boolean;
}

/**
 * Request that an entity walk a whole route and stop exactly on its last tile
 * (tap-to-walk, ADR-0101). Wraps {@link Mobile.setPath}: `path` is the ordered
 * tile list to step through, each tile 8-adjacent to the one before and the
 * first anchored on (or beside) the sender's resting tile. Unlike a held
 * {@link MoveIntentCommand} direction - which the server walks *continuously*
 * until a stop arrives, so arrival always overshoots by however far the
 * round-trip is - a route is finite: the mobile parks on the final tile with no
 * client involvement, which is what makes a tapped destination land exactly.
 * An empty `path` stops on the next tile boundary; a later `MoveIntent`
 * (keyboard/mouse steering) cancels the route.
 */
export interface WalkPathCommand {
  kind: 'WalkPath';
  entity: EntityId;
  /** The route's tiles in walk order (excluding nothing: the first tile anchors it). */
  path: TilePoint[];
}

/**
 * Use (toggle open/closed) a door entity. The `entity` is the **acting** mobile
 * (the player using the door), carried so the server's ownership gate can verify
 * the sender owns it exactly as it does for {@link MoveIntentCommand}; `door` is
 * the door entity to flip. Door state is authoritative shared-world state, so the
 * toggle happens once in the reducer and every viewer sees it in the next
 * snapshot (see ADR-0014).
 */
export interface UseDoorCommand {
  kind: 'UseDoor';
  /** The acting mobile (the player); the ownership check is against this id. */
  entity: EntityId;
  /** The door entity whose `open` flag this command toggles. */
  door: EntityId;
}

/**
 * Roll the dice-and-cup (dice-and-cup, ADR-0083). The `entity` is the **acting**
 * mobile (the player rolling), carried so the server's ownership gate can verify
 * the sender owns it exactly as it does for {@link MoveIntentCommand}; `dice` is
 * the dice prop entity being rolled, validated the way `UseDoor` validates its
 * `door` target.
 *
 * Unlike {@link UseDoorCommand}, rolling is **not** a sim state change: the roll
 * outcome is unseeded server randomness and the per-player cooldown reads the wall
 * clock, both of which `sim/` forbids (see `architecture.test.ts`). So the server
 * intercepts a valid `RollDice` and resolves it as an *ephemeral broadcast emote*
 * - exactly like speech (ADR-0010) - and never enqueues it; the reducer keeps only
 * a defensive no-op for it (so a stray enqueue can never throw the tick). The
 * command still rides the existing client-intent wire (a `ClientCommand`) so it
 * reuses the spoof-proof ownership gate, mirroring `UseDoor`'s shape.
 */
export interface RollDiceCommand {
  kind: 'RollDice';
  /** The acting mobile (the player); the ownership check is against this id. */
  entity: EntityId;
  /** The dice prop entity being rolled (validated to be a real dice). */
  dice: EntityId;
}

/**
 * Stomp a giant rat (Rat Swarm, PRD #222 / ADR-0119). The `entity` is the
 * **acting** mobile (the stomping player), carried so the server's ownership
 * gate can verify the sender owns it exactly as it does for
 * {@link MoveIntentCommand}; `rat` is the rat entity being stomped, validated
 * the way `UseDoor`/`RollDice` validate their targets (it must carry the `rat`
 * marker). `warMode` is the client-local war-mode stance assertion (the PRD's
 * Tab gate lives client-side in this version): the server passes it through to
 * `resolveStomp`, which refuses a stomp made out of war mode.
 *
 * Like {@link RollDiceCommand}, a stomp is **never enqueued**: the kill must
 * resolve against ephemeral event state (`RatSwarmState`), credit durable gold,
 * and fire broadcasts - all server acts, none of them sim state - so the server
 * intercepts a validated `Stomp` and resolves it off-queue via the pure
 * `resolveStomp` (ADR-0119). The reducer keeps only a defensive no-op for it,
 * and the command rides the existing client-intent wire so it reuses the
 * spoof-proof ownership gate.
 */
export interface StompCommand {
  kind: 'Stomp';
  /** The acting mobile (the stomping player); the ownership check is against this id. */
  entity: EntityId;
  /** The rat entity being stomped (validated to carry the `rat` marker). */
  rat: EntityId;
  /** The client's war-mode assertion; a stomp without it is refused server-side. */
  warMode: boolean;
}

/**
 * Loot the King's Justice thief's corpse (PRD #361 / ADR-0194). The `entity` is
 * the **acting** mobile (the looting player), carried so the server's ownership
 * gate verifies the sender owns it exactly as {@link StompCommand} does; `corpse`
 * is the fallen thief entity, validated the way `Stomp` validates its `rat` (it
 * must carry the `thiefCorpse` marker).
 *
 * Like {@link StompCommand}, a loot is **never enqueued**: it resolves against
 * ephemeral event state (`KingsJusticeState`), rolls unseeded server dice, credits
 * durable gold, and fires broadcasts - all server acts - so the server intercepts
 * a validated `LootCorpse` and resolves it off-queue via the pure `resolveLoot`
 * (range-checked against the looter's server-authoritative tile; first-come at the
 * server wins the race). The reducer keeps only a defensive no-op for it.
 */
export interface LootCorpseCommand {
  kind: 'LootCorpse';
  /** The acting mobile (the looting player); the ownership check is against this id. */
  entity: EntityId;
  /** The fallen thief entity (validated to carry the `thiefCorpse` marker). */
  corpse: EntityId;
}

/**
 * Issue a duel challenge to another player (Player duels, PRD #252 / ADR-0130).
 * The `entity` is the **acting** mobile (the challenger), carried so the server's
 * ownership gate verifies the sender owns it exactly as {@link StompCommand} does;
 * `opponent` is the challenged player entity, and `wager` the gold each side
 * stakes (`0` = an honor duel that escrows nothing).
 *
 * Like {@link StompCommand}, a challenge is **never enqueued**: it resolves
 * against ephemeral match state ({@link DuelMatch}) beside the world, validates
 * the target (self / rat / NPC / ghost / range / busy) and the wager (affordable,
 * reserved-only when staked) against live server state, and delivers an
 * accept/decline prompt to the target - all server acts, none of them sim state.
 * The server intercepts a validated `DuelChallenge` and resolves it off-queue via
 * `handleDuelChallenge`; the reducer keeps only a defensive no-op. It rides the
 * existing client-intent wire so it reuses the spoof-proof ownership gate.
 */
export interface DuelChallengeCommand {
  kind: 'DuelChallenge';
  /** The acting mobile (the challenger); the ownership check is against this id. */
  entity: EntityId;
  /** The challenged player entity (validated server-side, not a rat/NPC/ghost/self). */
  opponent: EntityId;
  /** Gold each side stakes; the pot is `2 * wager`. `0` = honor duel. */
  wager: number;
}

/**
 * Accept the pending duel challenge on this player (Player duels, PRD #252 /
 * ADR-0130). The `entity` is the accepting player (the challenge target), carried
 * for the ownership gate; the match it resolves is looked up server-side (a
 * player has at most one pending challenge), never named on the wire, so there is
 * nothing to spoof. Resolved off-queue like {@link StompCommand}: on accept both
 * stakes are escrowed and the fight begins (the five-count, then combat, death,
 * resurrect, and payout - Slice D / ADR-0131). The reducer keeps only a defensive
 * no-op.
 */
export interface DuelAcceptCommand {
  kind: 'DuelAccept';
  /** The accepting player (the pending challenge's target); ownership-checked. */
  entity: EntityId;
}

/**
 * Decline the pending duel challenge on this player (Player duels, PRD #252 /
 * ADR-0130). The mirror of {@link DuelAcceptCommand}: `entity` is the declining
 * player, the match is looked up server-side, and the server notifies the
 * challenger with a system journal line and tears the pending match down. No gold
 * moves (nothing was escrowed until accept). The reducer keeps a defensive no-op.
 */
export interface DuelDeclineCommand {
  kind: 'DuelDecline';
  /** The declining player (the pending challenge's target); ownership-checked. */
  entity: EntityId;
}

/** Issue a direct Sphere Duel challenge with an optional per-player gold stake. */
export interface SphereChallengeCommand {
  kind: 'SphereChallenge';
  entity: EntityId;
  opponent: EntityId;
  wager: number;
}

/** Accept this player's pending Sphere Duel challenge. */
export interface SphereAcceptCommand {
  kind: 'SphereAccept';
  entity: EntityId;
}

/** Decline this player's pending Sphere Duel challenge. */
export interface SphereDeclineCommand {
  kind: 'SphereDecline';
  entity: EntityId;
}

/** Acknowledge that this client's private Sphere arena scene is ready. */
export interface SphereReadyCommand {
  kind: 'SphereReady';
  entity: EntityId;
}

/** Begin one normalized Sphere spell cast against its automatic target. */
export interface SphereCastCommand {
  kind: 'SphereCast';
  entity: EntityId;
  spell: SphereSpellId;
  /** Match-scoped correlation token for immediate local presentation. */
  matchId?: number;
  /** Monotonically increasing within {@link matchId}. */
  sequence?: number;
}

/** Use one instant, virtual Sphere potion on the caster. */
export interface SpherePotionCommand {
  kind: 'SpherePotion';
  entity: EntityId;
  potion: SpherePotionId;
  matchId?: number;
  sequence?: number;
}

/** Cancel the sender's active Sphere spell cast. */
export interface SphereCancelCommand {
  kind: 'SphereCancel';
  entity: EntityId;
  matchId?: number;
  sequence?: number;
}

/** Request another round against the same Sphere Duel opponent and wager. */
export interface SphereRematchCommand {
  kind: 'SphereRematch';
  entity: EntityId;
}

/** Leave the Sphere Duel result screen and return both participants to town. */
export interface SphereExitCommand {
  kind: 'SphereExit';
  entity: EntityId;
}

/**
 * Issue a **dice-wager** challenge to another player (Dice Wager, ADR-0154). The mirror
 * of {@link DuelChallengeCommand} for a gambling match: `entity` is the **acting**
 * mobile (the challenger), carried so the server's ownership gate verifies the sender
 * owns it exactly as {@link DuelChallengeCommand} does; `opponent` is the challenged
 * player entity, and `wager` the gold each side stakes (`0` = an honor roll that
 * escrows nothing).
 *
 * A dice wager is "a duel resolved by dice instead of combat" (ADR-0154), so it reuses
 * the duel's off-queue discipline whole: like {@link DuelChallengeCommand} it is
 * **never enqueued** - it resolves against ephemeral match state
 * ({@link ../sim/diceWager.DiceWagerMatch}) beside the world, validates the target and
 * wager against the very same guards the duel uses, and delivers an accept/decline
 * prompt - all server acts, none of them sim state. The server intercepts a validated
 * `DiceWagerChallenge` and resolves it off-queue (`handleDiceWagerChallenge`); the
 * reducer keeps only a defensive no-op. It rides the existing client-intent wire so it
 * reuses the spoof-proof ownership gate.
 */
export interface DiceWagerChallengeCommand {
  kind: 'DiceWagerChallenge';
  /** The acting mobile (the challenger); the ownership check is against this id. */
  entity: EntityId;
  /** The challenged player entity (validated server-side, not a rat/NPC/ghost/self). */
  opponent: EntityId;
  /** Gold each side stakes; the pot is `2 * wager`. `0` = honor roll. */
  wager: number;
}

/**
 * Accept the pending dice-wager challenge on this player (Dice Wager, ADR-0154). The
 * mirror of {@link DuelAcceptCommand}: `entity` is the accepting player (the challenge
 * target), carried for the ownership gate; the match it resolves is looked up
 * server-side (a player has at most one pending challenge), never named on the wire, so
 * there is nothing to spoof. Resolved off-queue like {@link DuelAcceptCommand}: on
 * accept both stakes are escrowed (the reused duel escrow path) and the paced rolling
 * begins. The reducer keeps only a defensive no-op.
 */
export interface DiceWagerAcceptCommand {
  kind: 'DiceWagerAccept';
  /** The accepting player (the pending challenge's target); ownership-checked. */
  entity: EntityId;
}

/**
 * Decline the pending dice-wager challenge on this player (Dice Wager, ADR-0154). The
 * mirror of {@link DuelDeclineCommand}: `entity` is the declining player, the match is
 * looked up server-side, and the server tears the pending match down and narrates the
 * refusal. No gold moves (nothing was escrowed until accept). The reducer keeps a
 * defensive no-op.
 */
export interface DiceWagerDeclineCommand {
  kind: 'DiceWagerDecline';
  /** The declining player (the pending challenge's target); ownership-checked. */
  entity: EntityId;
}

/**
 * Enter a moongate (The Wandering Moongate, PRD #362 Slice A). The `entity` is the
 * **acting** mobile (the player entering), carried so the server's ownership gate can
 * verify the sender owns it exactly as {@link UseDoorCommand} does; `gate` is the
 * moongate entity being entered, validated the way `UseDoor` validates its `door`
 * (a real moongate within {@link ../sim/moongate.MOONGATE_ENTER_RANGE}).
 *
 * Like {@link RollDiceCommand}, entering is **never enqueued**: a transport gate's
 * teleport must force the predicting client onto the new pose (a server act - the
 * admin-teleport/teleporter-landing recipe), and a fate gate resolves against
 * ephemeral event state + server randomness + broadcasts, none of it sim state. So
 * the server intercepts a validated `EnterMoongate` and resolves it off-queue; the
 * reducer keeps only a defensive no-op. It rides the existing client-intent wire so
 * it reuses the spoof-proof ownership gate, mirroring `UseDoor`'s shape.
 */
export interface EnterMoongateCommand {
  kind: 'EnterMoongate';
  /** The acting mobile (the player entering); the ownership check is against this id. */
  entity: EntityId;
  /** The moongate entity being entered (validated to be a real, nearby moongate). */
  gate: EntityId;
}

/**
 * Open a treasure chest (The Wandering Moongate, PRD #362 Slice B). The `entity`
 * is the **acting** mobile, carried for the spoof-proof ownership gate; `chest`
 * is the chest entity being opened, validated the way `UseDoor` validates its
 * `door` (a real chest within reach on the actor's level).
 *
 * Like {@link EnterMoongateCommand}, opening is **never enqueued**: the purse
 * inside is ephemeral server event state (the Fortune shuffle) plus durable gold
 * plus broadcasts, none of it sim state, so the server intercepts a validated
 * `OpenChest` and resolves it off-queue; the reducer keeps only a defensive
 * no-op.
 */
export interface OpenChestCommand {
  kind: 'OpenChest';
  /** The acting mobile (the player opening); the ownership check is against this id. */
  entity: EntityId;
  /** The chest entity being opened (validated to be a real, nearby chest). */
  chest: EntityId;
}

/**
 * Buy one card pack (trading card game, PRD #493 Slice 4). The `entity` is the
 * **acting** mobile (the buyer), carried for the spoof-proof ownership gate exactly
 * as {@link OpenChestCommand} does. The command names no vendor: packs are bought
 * straight from the Card Binder's Packs tab, so - like {@link OpenCardPackCommand} -
 * the buyer's own account is the whole target and the shared owner/actor gate is
 * the whole shape check.
 *
 * Like {@link OpenChestCommand}, a purchase is **never enqueued**: it debits durable
 * gold, bumps the account's unopened-pack count, and echoes the owner-only
 * `CardState` - all server acts against the account store, none of it shared-world
 * sim state - so the server intercepts a validated `BuyCardPack` and resolves it
 * off-queue in the card event module; the reducer keeps only a defensive no-op.
 */
export interface BuyCardPackCommand {
  kind: 'BuyCardPack';
  /** The acting mobile (the buying player); the ownership check is against this id. */
  entity: EntityId;
}

/**
 * Open one unopened card pack (trading card game, PRD #493 Slice 4). The `entity`
 * is the **acting** mobile (the opener); the command names **no** target - the
 * opener's own account holds the pack count, so the server resolves it from the
 * connection's `accountKey`, the way {@link FireBowCommand} carries only the actor.
 *
 * Like {@link BuyCardPackCommand}, opening is **never enqueued**: it rolls five
 * cards from server-fed seeded RNG (the pure {@link ./cardPacks.rollPack} seam),
 * banks them into the collection, decrements the pack count, and pushes the
 * owner-only reveal + `CardState` - all server acts, none of it sim state - so the
 * card event module resolves a validated `OpenCardPack` off-queue and the reducer
 * keeps only a defensive no-op.
 */
export interface OpenCardPackCommand {
  kind: 'OpenCardPack';
  /** The acting mobile (the opening player); the ownership check is against this id. */
  entity: EntityId;
}

/**
 * Challenge another player to a **card duel** (trading card game, PRD #493 Slice 5).
 * The card-game twin of {@link DuelChallengeCommand} / {@link DiceWagerChallengeCommand}:
 * `entity` is the **acting** mobile (the challenger), carried for the spoof-proof
 * ownership gate; `opponent` is the challenged player entity (validated server-side to
 * be a live, nearby, reserved player - card duels are reserved-only, like gold); `wager`
 * is the gold each side stakes (`0` = an honor card duel).
 *
 * Like {@link DuelChallengeCommand}, a card duel is **never enqueued**: the whole match
 * (challenge / accept / the turn-based battle / wager escrow / payout) resolves against
 * ephemeral match state (the pure {@link ./cardGame.CardMatch} engine) beside the world +
 * durable gold + owner-only pushes, all server-side, in the card-game event module
 * (`server/events/cardGame.ts`). The reducer keeps only a defensive no-op.
 */
export interface CardDuelChallengeCommand {
  kind: 'CardDuelChallenge';
  /** The acting mobile (the challenger); the ownership check is against this id. */
  entity: EntityId;
  /** The challenged player entity (validated server-side, not a rat/NPC/ghost/self). */
  opponent: EntityId;
  /** Gold each side stakes; the pot is `2 * wager`. `0` = honor card duel. */
  wager: number;
  /** Index of the challenger's saved deck, selected before issuing the challenge. */
  deckIndex?: number;
}

/**
 * Accept the pending card-duel challenge on this player (trading card game, PRD #493
 * Slice 5). The mirror of {@link DuelAcceptCommand}: `entity` is the accepting player
 * (the challenge target), carried for the ownership gate; the match it resolves is
 * looked up server-side (a player has at most one pending challenge), never named on the
 * wire. On accept both stakes escrow and the battle begins. The reducer keeps a
 * defensive no-op.
 */
export interface CardDuelAcceptCommand {
  kind: 'CardDuelAccept';
  /** The accepting player (the pending challenge's target); ownership-checked. */
  entity: EntityId;
  /** Index of the accepting player's saved deck, selected before the duel starts. */
  deckIndex?: number;
}

/**
 * Decline the pending card-duel challenge on this player (trading card game, PRD #493
 * Slice 5). The mirror of {@link DuelDeclineCommand}: `entity` is the declining player,
 * the match is looked up server-side, and the server tears the pending match down and
 * notifies the challenger. No gold moves (nothing was escrowed until accept). The reducer
 * keeps a defensive no-op.
 */
export interface CardDuelDeclineCommand {
  kind: 'CardDuelDecline';
  /** The declining player (the pending challenge's target); ownership-checked. */
  entity: EntityId;
}

/** Publish an ephemeral, server-wide invitation to an honor or wagered card duel. */
export interface CardDuelListingCreateCommand {
  kind: 'CardDuelListingCreate';
  entity: EntityId;
  /** The host's saved deck. This remains private and is never included in listing snapshots. */
  deckIndex: number;
  /** Gold staked by each player; zero advertises an honor duel. */
  wager: number;
}

/** Withdraw the acting player's current card-duel listing, if any. */
export interface CardDuelListingCancelCommand {
  kind: 'CardDuelListingCancel';
  entity: EntityId;
}

/** Accept one exact listing with the acting player's selected saved deck. */
export interface CardDuelListingAcceptCommand {
  kind: 'CardDuelListingAccept';
  entity: EntityId;
  /** Opaque listing identity; stale ids never resolve to replacement terms. */
  listingId: string;
  deckIndex: number;
}

/**
 * One in-match move a card duelist submits (trading card game, PRD #493 Slice 5). The
 * seat-less wire twin of a {@link ./cardGame.CardCommand}: the client never names its
 * own seat (the server derives it from the connection's match membership, so it can only
 * ever act as its own seat), it just names the move. The server maps the accepted move
 * onto the pure engine via {@link ./cardGame.applyCardCommand}; an illegal move (not your
 * turn, bad target) is rejected by the engine and dropped.
 */
export type CardDuelMove =
  | { kind: 'Mulligan'; instanceIds: number[] }
  | { kind: 'PlayCard'; instanceId: number; target?: CardTarget }
  | { kind: 'Attack'; attackerId: number; target: CardTarget }
  | { kind: 'EndTurn' }
  | { kind: 'Concede' };

/**
 * Submit one in-match card-duel move (trading card game, PRD #493 Slice 5). `entity` is
 * the **acting** mobile (the player), carried for the ownership gate; `move` is the
 * seat-less {@link CardDuelMove}. Like {@link DuelAcceptCommand}, the match is looked up
 * server-side and the move resolved off-queue in the card-game event module; the reducer
 * keeps a defensive no-op.
 */
export interface CardDuelActionCommand {
  kind: 'CardDuelAction';
  /** The acting mobile (the playing player); the ownership check is against this id. */
  entity: EntityId;
  /** The seat-less move the engine applies for this player's seat. */
  move: CardDuelMove;
}

/**
 * Start a **free AI practice match** against Card Master Carl (trading card game, PRD
 * #493 Slice 6). The tutorial-gentle, no-stakes way to learn the card game before
 * challenging real players: `entity` is the **acting** mobile (the learner), carried for
 * the spoof-proof ownership gate exactly as {@link BuyCardPackCommand} does. Carl is a
 * pure AI persona, not a world entity, so the command names no vendor and practice
 * starts from the Binder anywhere in the world.
 *
 * Practice is a card duel resolved against the greedy card AI instead of another player,
 * so it **reuses the whole Slice-5 card-duel machinery** ({@link CardDuelActionCommand}
 * for the learner's moves, the same battle gump, match slot, and dueling busy role) -
 * it is not a parallel battle path. Like {@link CardDuelChallengeCommand} it is **never
 * enqueued**: the match resolves against ephemeral state (the pure {@link ./cardGame.CardMatch}
 * engine) beside the world, in the card-game event module (`server/events/cardGame.ts`).
 * Ordinary practice moves no gold and records no win/loss.
 * A date-scoped `dailyChallengeId` keeps the same engine but lets the server award one
 * bounded first-win reward to a reserved account.
 * The reducer keeps only a defensive no-op.
 */
export interface CardDuelPracticeCommand {
  kind: 'CardDuelPractice';
  /** The acting mobile (the learning player); the ownership check is against this id. */
  entity: EntityId;
  /** Practice-only loaner for the learner (never checked against or written to ownership). */
  learnerRecipeId?: StarterRecipeId;
  /** Saved-deck slot used by post-tutorial practice; mutually exclusive with a loaner. */
  learnerDeckIndex?: number;
  /** Explicit Carl matchup; absent asks the server for its seeded rotation. */
  opponentRecipeId?: StarterRecipeId;
  /** Current server-authored daily challenge id; requires `learnerDeckIndex`. */
  dailyChallengeId?: string;
}

/**
 * The three Bank Escort interactions (PRD #371): ask the escort its destination,
 * accept the job, or hand it back. The `entity` is the **acting** mobile (the
 * player), carried for the spoof-proof ownership gate exactly as
 * {@link LootCorpseCommand} does; `npc` is the escort entity, validated to carry
 * the `escort` component the way a loot validates `thiefCorpse`.
 *
 * Like {@link LootCorpseCommand}, none of the three is ever enqueued: they
 * resolve against ephemeral escort state (`EscortState`) + durable gold +
 * broadcasts - all server acts - so the server intercepts a validated escort
 * command and resolves it off-queue via the pure `resolveEscortAsk` /
 * `resolveEscortAccept` / `resolveEscortAbandon` (range-checked against the
 * actor's server-authoritative tile; first accept at the server wins the race).
 * The reducer keeps only defensive no-ops.
 */
export interface EscortAskCommand {
  kind: 'EscortAsk';
  /** The acting mobile (the asking player); the ownership check is against this id. */
  entity: EntityId;
  /** The escort entity (validated to carry the `escort` component). */
  npc: EntityId;
}

/** Accept the escort job (see {@link EscortAskCommand} for the shared shape). */
export interface EscortAcceptCommand {
  kind: 'EscortAccept';
  /** The acting mobile (the accepting player); the ownership check is against this id. */
  entity: EntityId;
  /** The escort entity (validated to carry the `escort` component). */
  npc: EntityId;
}

/** Hand an accepted escort job back (see {@link EscortAskCommand} for the shared shape). */
export interface EscortAbandonCommand {
  kind: 'EscortAbandon';
  /** The acting mobile (the current escorter); the ownership check is against this id. */
  entity: EntityId;
  /** The escort entity (validated to carry the `escort` component). */
  npc: EntityId;
}

/**
 * The wire ids of the player emotes (ADR-0144): the social gestures a player triggers by
 * hotkey (`bow`, `salute`) or - since ADR-0387 - by typing an `*action*` chat line
 * (`dances`). Each is also the baked clip name broadcast on the
 * {@link ../net/protocol.ActionAnimationMessage} (`bow` = UO anim action 32,
 * `salute` = action 33, `dances` = action 30), so this type is derived from the single
 * {@link EMOTE_CLIP_IDS} source in `content` - the wire contract and the render/bake set
 * can never drift.
 */
export type EmoteId = (typeof EMOTE_CLIP_IDS)[number];

/** Whether `value` is a known {@link EmoteId} (used by the server's validation gate). */
export function isEmoteId(value: unknown): value is EmoteId {
  return typeof value === 'string' && EMOTE_CLIP_IDS.includes(value);
}

/**
 * Play a social **emote** on the acting player (Emotes, ADR-0144). The `entity` is the
 * **acting** mobile (the player emoting), carried so the server's ownership gate can
 * verify the sender owns it exactly as {@link RollDiceCommand} does; `emote` names which
 * gesture ({@link EmoteId}) - `bow` (UO action 32) or `salute` (action 33). The `dances`
 * emote (action 30, ADR-0387) is a valid id here too, but ships with no hotkey: it is
 * triggered by a typed `*dances*` chat line the server resolves itself, with no command.
 *
 * Like {@link RollDiceCommand}, an emote is **not** sim state: it is an ephemeral body
 * animation gated by a wall-clock cooldown, neither of which belongs in `sim/`. So the
 * server intercepts a validated `Emote` and resolves it off-queue - it rebroadcasts an
 * {@link ../net/protocol.ActionAnimationMessage} that plays the named clip on the actor's
 * sprite for every client (once, or the gesture's {@link ../sim/content.emoteRepeat} loop
 * count for a repeating one), exactly the path the stomp swing and duel combat use
 * - and never enqueues it; the reducer keeps only a defensive no-op (so a stray enqueue
 * can never throw the tick). It rides the existing client-intent wire (a `ClientCommand`)
 * so it reuses the spoof-proof ownership gate, mirroring `RollDice`'s shape.
 */
export interface EmoteCommand {
  kind: 'Emote';
  /** The acting mobile (the player emoting); the ownership check is against this id. */
  entity: EntityId;
  /** Which gesture to play - the wire clip name broadcast to every client. */
  emote: EmoteId;
}

/**
 * Fire a **bow shot** at your duel opponent (Fire your bow!, ADR-0255). The `entity`
 * is the **acting** mobile (the shooter), carried so the server's ownership gate can
 * verify the sender owns it exactly as {@link EmoteCommand} does. It names **no**
 * opponent: the server resolves the target from the shooter's live duel match (a
 * player has at most one match), the way {@link DuelAcceptCommand} looks its match up
 * server-side, so there is nothing to spoof.
 *
 * Like {@link EmoteCommand}, a bow shot is **not** sim state: it resolves against the
 * ephemeral duel match + the shared combat seam + broadcasts, all server-side. The
 * server intercepts a validated `FireBow` and resolves it off-queue (in the duel event
 * module's `handleFireBow`) as a **toggle** of the shooter's bow **auto-fire**: the
 * first press arms it (loosing one shot at once), a second press disarms it, and while
 * armed the fight tick repeats the shot at the legal cadence on its own - the ranged
 * twin of the auto-swing, so the player never mashes F. Each actual shot rolls the
 * 50/50 hit on the injected `duelRng`, animates the shoot clip, and applies any damage;
 * the reducer keeps only a defensive no-op. It rides the existing client-intent wire so
 * it reuses the spoof-proof ownership gate.
 */
export interface FireBowCommand {
  kind: 'FireBow';
  /** The acting mobile (the shooter); the ownership check is against this id. */
  entity: EntityId;
}

/**
 * Play a **moonwalk** on the acting player (Moonwalk, ADR-0145). The `entity` is the
 * **acting** mobile (the player moonwalking), carried so the server's ownership gate can
 * verify the sender owns it exactly as {@link MoveIntentCommand} does.
 *
 * Unlike {@link EmoteCommand} / {@link RollDiceCommand} - ephemeral animations the server
 * rebroadcasts off-queue - a moonwalk is **real sim state**: it glides the mobile
 * backward (opposite its facing) up to {@link ../sim/moonwalk.MOONWALK_TILES} tiles over
 * many ticks while the walk clip plays, honouring walkability the whole way. So it *is*
 * enqueued and applied through the reducer ({@link Mobile.startMoonwalk}) - like
 * {@link MoveIntentCommand} - and the resulting motion rides the ordinary snapshot stream,
 * so every client in the room sees the glide. The authority gates it *before* the queue
 * (a per-player wall-clock cooldown + a no-mid-duel check), the same seam `UseDoor`'s
 * cooldown uses; the wire carries only kind + actor (the fixed tile count and slower gait
 * live in the sim).
 */
export interface MoonwalkCommand {
  kind: 'Moonwalk';
  /** The acting mobile (the player moonwalking); the ownership check is against this id. */
  entity: EntityId;
}

/**
 * Start an Arena run (Arena Slice D, PRD #334): the acting player steps through
 * the Arena Gate into a private, instanced run world. Like {@link StompCommand},
 * this is **not** sim state - the server resolves it off-queue (it removes the
 * town entity, builds the run's seeded world, and re-routes the socket's
 * snapshot stream), so it never enters any world's queue. The wire carries only
 * kind + actor; everything else (the arena map, the seed, the spawn) is the
 * server's.
 */
export interface StartArenaRunCommand {
  kind: 'StartArenaRun';
  /** The acting mobile (the player entering); the ownership check is against this id. */
  entity: EntityId;
  /**
   * The chosen Arena class (PRD #446): absent = the fire mage (the default,
   * so existing clients and replays are unchanged). The server validates it
   * against the class registry and refuses an unknown id in fiction (the
   * crowded-gate pattern) - it is carried as a bare string on the wire and
   * narrowed server-side, never trusted as a class id here.
   */
  classId?: string;
  /** Absent is Standard for backward compatibility; the server validates availability. */
  challengeId?: string;
}

/** The optional authored dungeon mode used for focused combat tuning. */
export type DungeonRunMode = 'combat-lab';

/** Enter a private generated dungeon run or the authored combat lab. */
export interface StartDungeonRunCommand {
  kind: 'StartDungeonRun';
  entity: EntityId;
  /** The nearby permanent entrance that authorized an ordinary dungeon selection flow. */
  gate?: EntityId;
  /** The owned Soulstone whose Hero is selected for this run (PRD #837). */
  soulstoneId: string;
  /** Absent for an ordinary generated dungeon. */
  mode?: DungeonRunMode;
  /** Named authored setup within the focused combat lab. */
  labScenario?: CombatLabScenarioId;
  /** Optional exact replay seed; absent lets the authoritative server mint one. */
  seed?: number;
  /** Exact replay generator version; absent selects the current version. */
  generatorVersion?: number;
  /**
   * The declared campaign dungeon node to enter (PRD #837 slice 2). Absent
   * keeps the ordinary generated run. Carried as a bare string on the wire and
   * resolved server-side against the campaign definition, never trusted.
   */
  campaignNodeId?: string;
  /**
   * The difficulty tier to play the campaign node at (PRD #837 slice 7).
   * Absent is Normal. A bare string on the wire, narrowed server-side against
   * the tier ids and the Soulstone's own derived unlocks, never trusted.
   */
  campaignTier?: string;
}

/** Leave the owning private dungeon without completing it. */
export interface AbandonDungeonRunCommand {
  kind: 'AbandonDungeonRun';
  entity: EntityId;
}

/** Fire the active dungeon run's one cursor-aimed Warrior ability. */
export interface CastDungeonAbilityCommand {
  kind: 'CastDungeonAbility';
  entity: EntityId;
  /** Client-local cast sequence used only to reconcile optimistic feedback. */
  seq: number;
  /** Defaults to the Hero's primary attack for older clients. */
  abilityId?: string;
  /** Idempotency key shared by the predicted swing and its server echo. */
  actionId?: string;
  /** Authoritative continuous world point the cursor aimed through. */
  aim: { x: number; y: number };
  /** Ordered continuous-movement time consumed before a Charge begins. */
  movement?: {
    seq: number;
    elapsedMs: number;
  };
}

/** Pick one authoritative private-run ground item into the Hero's backpack. */
export interface PickupDungeonLootCommand {
  kind: 'PickupDungeonLoot';
  entity: EntityId;
  loot: EntityId;
}

/**
 * Use the owning private run's campaign waypoint (PRD #837 slice 4): teleport
 * to town and durably unlock the run's campaign node for the taking Soulstone
 * at the run's difficulty. Validated like `PickupDungeonLoot` - the target must
 * be a real waypoint entity in the sender's own run, within the shared prop
 * reach - and resolved off-queue by the dungeon service, which owns both the
 * durable unlock and the return-to-town.
 */
export interface UseDungeonWaypointCommand {
  kind: 'UseDungeonWaypoint';
  entity: EntityId;
  waypoint: EntityId;
}

/**
 * Pick one option of the pending Arena level-up draft (Arena Slice D, PRD #334).
 * Resolved off-queue against the sender's **own** run only (the session -> run
 * map is the ownership; a player can never affect another's run), through the
 * sim's {@link ../sim/arena.applyArenaPick} - which rejects a pick with no
 * pending draft, an out-of-range index, or a stale option, leaving the pause
 * untouched. Never enqueued; the run world is private server state.
 */
export interface PickArenaOptionCommand {
  kind: 'PickArenaOption';
  /** The acting mobile (the runner); the ownership check is against this id. */
  entity: EntityId;
  /** Index into the pending {@link ../sim/arena.ArenaDraftOffer}'s options. */
  index: number;
}

/**
 * The closed set of commands the world understands. The reducer's exhaustive
 * `switch` forces each to be handled.
 */
export type Command =
  | MoveIntentCommand
  | WalkPathCommand
  | UseDoorCommand
  | RollDiceCommand
  | StompCommand
  | LootCorpseCommand
  | EscortAskCommand
  | EscortAcceptCommand
  | EscortAbandonCommand
  | DuelChallengeCommand
  | DuelAcceptCommand
  | DuelDeclineCommand
  | SphereChallengeCommand
  | SphereAcceptCommand
  | SphereDeclineCommand
  | SphereReadyCommand
  | SphereCastCommand
  | SpherePotionCommand
  | SphereCancelCommand
  | SphereRematchCommand
  | SphereExitCommand
  | DiceWagerChallengeCommand
  | DiceWagerAcceptCommand
  | DiceWagerDeclineCommand
  | EnterMoongateCommand
  | OpenChestCommand
  | BuyCardPackCommand
  | OpenCardPackCommand
  | CardDuelChallengeCommand
  | CardDuelAcceptCommand
  | CardDuelDeclineCommand
  | CardDuelListingCreateCommand
  | CardDuelListingCancelCommand
  | CardDuelListingAcceptCommand
  | CardDuelActionCommand
  | CardDuelPracticeCommand
  | EmoteCommand
  | FireBowCommand
  | MoonwalkCommand
  | StartArenaRunCommand
  | StartDungeonRunCommand
  | AbandonDungeonRunCommand
  | CastDungeonAbilityCommand
  | PickupDungeonLootCommand
  | UseDungeonWaypointCommand
  | PickArenaOptionCommand;

/**
 * Pure reducer: apply a single command to the world. Called by {@link World.tick}
 * for each drained command in order. Reducing an absent or component-less target
 * is a no-op (commands can outlive the entity they name), so the tick never
 * throws on stale input. It reads no wall clock and no unseeded randomness.
 */
export function applyCommand(world: World, cmd: Command): void {
  switch (cmd.kind) {
    case 'MoveIntent': {
      world.get(cmd.entity)?.mobile?.setIntent(cmd.dir, cmd.running, cmd.turnOnly);
      return;
    }
    case 'WalkPath': {
      world.get(cmd.entity)?.mobile?.setPath(cmd.path);
      return;
    }
    case 'UseDoor': {
      // Flip the authoritative open/closed flag. Reducing an absent door (or an
      // entity that is not a door) is a no-op, like every other command, so a
      // stale or spoofed target never throws the tick.
      const door = world.get(cmd.door)?.door;
      if (door) door.open = !door.open;
      return;
    }
    case 'RollDice': {
      // Intentional no-op: a dice roll is a server-authoritative *ephemeral
      // effect* (unseeded randomness + a wall-clock cooldown + a broadcast emote),
      // not sim state - the server resolves it off-sim and never enqueues it
      // (ADR-0083), the same way speech never enters the world. This case exists
      // only to keep the reducer's exhaustive switch total and to stay a safe
      // no-op should a `RollDice` ever reach the queue (like every other command).
      return;
    }
    case 'Stomp': {
      // Intentional no-op, the `RollDice` pattern: a stomp resolves against
      // ephemeral event state + durable gold + broadcasts (ADR-0119), all of
      // which live server-side - the server intercepts a validated `Stomp` and
      // resolves it off-queue via `resolveStomp`, never enqueuing it. This case
      // exists only to keep the exhaustive switch total and to stay a safe
      // no-op should a stray `Stomp` ever reach the queue.
      return;
    }
    case 'LootCorpse': {
      // Intentional no-op, the `Stomp` pattern (ADR-0194): a corpse loot resolves
      // against ephemeral event state + an unseeded server dice roll + durable
      // gold + broadcasts, all server-side - the server intercepts a validated
      // `LootCorpse` and resolves it off-queue via `resolveLoot`, never enqueuing
      // it. This case exists only to keep the exhaustive switch total and to stay
      // a safe no-op should a stray `LootCorpse` ever reach the queue.
      return;
    }
    case 'EscortAsk':
    case 'EscortAccept':
    case 'EscortAbandon': {
      // Intentional no-ops, the `LootCorpse` pattern (PRD #371): every escort
      // interaction resolves against ephemeral escort state + durable gold +
      // broadcasts, all server-side - the server intercepts a validated escort
      // command and resolves it off-queue (`resolveEscortAsk` /
      // `resolveEscortAccept` / `resolveEscortAbandon`), never enqueuing it.
      // These cases exist only to keep the exhaustive switch total and to stay
      // safe no-ops should a stray escort command ever reach the queue.
      return;
    }
    case 'DuelChallenge':
    case 'DuelAccept':
    case 'DuelDecline': {
      // Intentional no-op, the `Stomp` pattern (ADR-0130): the whole duel
      // handshake resolves against ephemeral match state + durable gold escrow +
      // owner-only prompts, all server-side - the server intercepts a validated
      // duel command and resolves it off-queue (`handleDuelChallenge` /
      // `handleDuelAccept` / `handleDuelDecline`), never enqueuing it. These
      // cases exist only to keep the exhaustive switch total and to stay safe
      // no-ops should a stray duel command ever reach the queue.
      return;
    }
    case 'SphereChallenge':
    case 'SphereAccept':
    case 'SphereDecline':
    case 'SphereReady':
    case 'SphereCast':
    case 'SpherePotion':
    case 'SphereCancel':
    case 'SphereRematch':
    case 'SphereExit': {
      // Intentional no-op: Sphere Duel challenge orchestration and its private,
      // normalized match engine live in the server event module. The module
      // validates and resolves these commands off the shared-world queue.
      return;
    }
    case 'DiceWagerChallenge':
    case 'DiceWagerAccept':
    case 'DiceWagerDecline': {
      // Intentional no-op, the `DuelChallenge` pattern (ADR-0154): the dice-wager
      // handshake resolves against ephemeral match state + durable gold escrow +
      // owner-only prompts, all server-side - the server intercepts a validated
      // dice-wager command and resolves it off-queue (`handleDiceWagerChallenge` /
      // `handleDiceWagerAccept` / `handleDiceWagerDecline`), never enqueuing it. These
      // cases exist only to keep the exhaustive switch total and to stay safe no-ops
      // should a stray dice-wager command ever reach the queue.
      return;
    }
    case 'OpenChest': {
      // Intentional no-op, the `EnterMoongate` pattern (PRD #362): the purse
      // inside a chest is ephemeral server event state + durable gold +
      // broadcasts, resolved off-queue by the server. This case exists only to
      // keep the exhaustive switch total and to stay a safe no-op should a stray
      // `OpenChest` ever reach the queue.
      return;
    }
    case 'EnterMoongate': {
      // Intentional no-op, the `RollDice` pattern (PRD #362): a moongate entry is
      // resolved off-queue by the server - a transport gate's teleport must force
      // the predicting client onto the new pose, and a fate gate resolves against
      // ephemeral event state + server randomness, none of it sim state. This case
      // exists only to keep the exhaustive switch total and to stay a safe no-op
      // should a stray `EnterMoongate` ever reach the queue.
      return;
    }
    case 'BuyCardPack':
    case 'OpenCardPack': {
      // Intentional no-op, the `OpenChest` pattern (trading card game, PRD #493
      // Slice 4): buying and opening a pack move durable gold + the account's
      // collection/pack count + owner-only pushes, resolved off-queue by the card
      // event module. These cases exist only to keep the exhaustive switch total
      // and to stay safe no-ops should a stray card command ever reach the queue.
      return;
    }
    case 'CardDuelChallenge':
    case 'CardDuelAccept':
    case 'CardDuelDecline':
    case 'CardDuelListingCreate':
    case 'CardDuelListingCancel':
    case 'CardDuelListingAccept':
    case 'CardDuelAction':
    case 'CardDuelPractice': {
      // Intentional no-op, the `DuelChallenge` pattern (trading card game, PRD #493
      // Slice 5/6): the whole card-duel handshake + turn-based battle (PvP or the free
      // AI practice match) resolves against ephemeral match state (the pure `cardGame`
      // engine) + owner-only pushes (+ durable gold escrow for a staked PvP duel), all
      // server-side - the card-game event module intercepts a validated card-duel command
      // and resolves it off-queue, never enqueuing it. These cases exist only to keep the
      // exhaustive switch total and to stay safe no-ops should a stray card-duel command
      // ever reach the queue.
      return;
    }
    case 'Emote': {
      // Intentional no-op, the `RollDice` pattern (ADR-0144): an emote is an
      // ephemeral body animation the server rebroadcasts off-queue (an
      // `ActionAnimation`), never sim state, so it never enters the world. This
      // case exists only to keep the exhaustive switch total and to stay a safe
      // no-op should a stray `Emote` ever reach the queue.
      return;
    }
    case 'FireBow': {
      // Intentional no-op, the `Emote` pattern (ADR-0255): a duel bow shot resolves
      // off-queue against the ephemeral duel match + the shared combat seam +
      // broadcasts (the shoot clip + a damage number), none of it sim state, so it
      // never enters the world queue. This case exists only to keep the exhaustive
      // switch total and to stay a safe no-op should a stray `FireBow` ever reach it.
      return;
    }
    case 'Moonwalk': {
      // Unlike the ephemeral emotes, a moonwalk *is* sim state (ADR-0145): begin the
      // backward glide on the actor's mobile, which the movement system then drives
      // tile-by-tile over the next ticks. Reducing an absent or mobile-less target is a
      // no-op, like every other command, so a stale/spoofed actor never throws the tick.
      // The authority has already gated the cooldown + no-mid-duel rule before enqueue.
      world.get(cmd.entity)?.mobile?.startMoonwalk();
      return;
    }
    case 'StartArenaRun':
    case 'StartDungeonRun':
    case 'AbandonDungeonRun':
    case 'CastDungeonAbility':
    case 'PickupDungeonLoot':
    case 'UseDungeonWaypoint':
    case 'PickArenaOption': {
      // Intentional no-op, the `Stomp` pattern (Arena Slice D): both Arena
      // commands resolve off-queue against the server's private session -> run
      // map (entering builds a whole second world; a pick mutates ephemeral run
      // state via `applyArenaPick`), none of it shared-world sim state. These
      // cases exist only to keep the exhaustive switch total and to stay safe
      // no-ops should a stray Arena command ever reach a queue.
      return;
    }
  }
}
